import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/** Ширина колонки плитки, как была у CSS columns. */
const COLUMN_PX = 190;
const GAP_PX = 12;

/**
 * Плитка своей раскладкой, а не CSS columns: те льют сверху вниз по колонке.
 * Здесь каждый следующий элемент идёт в самую короткую колонку — порядок
 * читается по рядам, как у Pinterest. `heightOf` — высота плитки в ширинах
 * колонки (картинка по пропорциям + подпись).
 */
export function Masonry<T>({
  items,
  heightOf,
  keyOf,
  renderTile,
  annot = "сетка референсов",
}: {
  items: readonly T[];
  heightOf: (item: T) => number;
  keyOf: (item: T) => string | number;
  renderTile: (item: T, index: number) => ReactNode;
  /** Значение data-annot корневого узла сетки. */
  annot?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(4);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setCount(Math.max(1, Math.floor((el.clientWidth + GAP_PX) / (COLUMN_PX + GAP_PX))));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columns = useMemo(() => {
    const cols: { index: number; item: T }[][] = Array.from({ length: count }, () => []);
    const height = new Array<number>(count).fill(0);
    items.forEach((item, index) => {
      let shortest = 0;
      for (let c = 1; c < count; c++) if (height[c]! < height[shortest]!) shortest = c;
      cols[shortest]!.push({ index, item });
      height[shortest]! += heightOf(item);
    });
    return cols;
  }, [items, count, heightOf]);

  return (
    <div ref={box} className="flex items-start gap-3" data-annot={annot}>
      {columns.map((col, c) => (
        <div key={c} className="grid min-w-0 flex-1 gap-3">
          {col.map(({ index, item }) => (
            <Fragment key={keyOf(item)}>{renderTile(item, index)}</Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}
